import analytics from "@/utils/analytics";
import {
  CompleteConnectorOAuthRequest,
  ConnectConnectorRequest,
  ConnectConnectorResult,
  Connector,
  ConnectorDiscovery,
  ConnectorPayload,
  ConnectorTool,
  OrgConnector,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

type ConnectConnectorVariables = ConnectConnectorRequest & {
  connectorId: string;
};

export function useConnectors(
  options?: Partial<UseQueryOptions<Connector[], AxiosError>>,
) {
  return useQuery<Connector[], AxiosError>({
    queryKey: ["connectors"],
    queryFn: async () => {
      return await orgAPI.connectors.list();
    },
    ...options,
  });
}

/**
 * The tools a connector lists through the user's connection. Keyed by the connection too, since
 * every connect makes a new one, so a reconnect never shows the last connection's tools or error.
 * Each fetch reaches the server live, so not persisted or refetched on focus, and not retried: an
 * attempt can take the API's whole 10s budget, and the pane offers a retry.
 */
export function useConnectorTools(
  connectorId: string,
  connectionId: string | undefined,
  options?: Partial<UseQueryOptions<ConnectorTool[], AxiosError>>,
) {
  return useQuery<ConnectorTool[], AxiosError>({
    queryKey: ["connectors", connectorId, "tools", connectionId ?? null],
    queryFn: () => orgAPI.connectors.tools(connectorId),
    retry: false,
    refetchOnWindowFocus: false,
    meta: { persist: false },
    ...options,
  });
}

/** Leaves the list to the caller: an OAuth connection lands only once it is completed. */
export function useConnectConnector(
  options?: UseMutationOptions<
    ConnectConnectorResult,
    AxiosError,
    ConnectConnectorVariables
  >,
) {
  return useMutation<
    ConnectConnectorResult,
    AxiosError,
    ConnectConnectorVariables
  >({
    mutationFn: async ({ connectorId, ...data }) => {
      return await orgAPI.connectors.connect(connectorId, data);
    },
    ...options,
  });
}

/** Finishes an OAuth connect with the code and state its callback handed back. */
export function useCompleteConnectorOAuth() {
  const queryClient = useQueryClient();
  return useMutation<
    ConnectConnectorResult,
    AxiosError,
    CompleteConnectorOAuthRequest & { connector: Connector }
  >({
    mutationFn: ({ connector, ...data }) =>
      orgAPI.connectors.completeOAuth(connector.id, data),
    onSuccess: (_, { connector }) => {
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      analytics.trackConnectorConnected({
        connectorId: connector.id,
        slug: connector.slug,
        authMethod: connector.auth_method,
        reconnect: !!connector.connection,
      });
    },
  });
}

export function useDisconnectConnector(
  options?: UseMutationOptions<void, AxiosError, Connector>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, Connector>({
    mutationFn: (connector) =>
      orgAPI.connectors.disconnect(connector.connection!.id),
    onSuccess: (_, connector) => {
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      analytics.trackConnectorDisconnected({
        connectorId: connector.id,
        slug: connector.slug,
      });
    },
    ...options,
  });
}

/** Creates or updates an organization's connector, or one of the user's own, which may override a shared one's slug for them. */
export function useSaveConnector(owner: "organization" | "user") {
  const queryClient = useQueryClient();
  return useMutation<
    Connector | OrgConnector,
    AxiosError,
    ConnectorPayload & { connectorId?: string }
  >({
    mutationFn: ({ connectorId, ...data }) => {
      const api = orgAPI.connectors;
      if (owner === "organization") {
        return connectorId
          ? api.orgUpdate(connectorId, data)
          : api.orgCreate(data);
      }
      return connectorId ? api.update(connectorId, data) : api.create(data);
    },
    onSuccess: () => {
      // A member's list carries the organization's connectors too.
      if (owner === "organization") {
        queryClient.invalidateQueries({ queryKey: ["org-connectors"] });
      }
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
    },
  });
}

/** Probes an MCP server for what a new connector of `owner`'s to it needs. */
export function useDiscoverConnector(owner: "organization" | "user") {
  return useMutation<ConnectorDiscovery, AxiosError, string>({
    mutationFn: (url) =>
      owner === "organization"
        ? orgAPI.connectors.orgDiscover(url)
        : orgAPI.connectors.discover(url),
  });
}

export function useDeleteConnector() {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, Connector>({
    mutationFn: (connector) => orgAPI.connectors.delete(connector.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
    },
  });
}

/** The organization's own connectors, for its admins to manage. */
export function useOrgConnectors(
  options?: Partial<UseQueryOptions<OrgConnector[], AxiosError>>,
) {
  return useQuery<OrgConnector[], AxiosError>({
    queryKey: ["org-connectors"],
    queryFn: async () => {
      return await orgAPI.connectors.orgList();
    },
    ...options,
  });
}

export function useDeleteOrgConnector() {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, OrgConnector>({
    mutationFn: (connector) => orgAPI.connectors.orgDelete(connector.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-connectors"] });
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
    },
  });
}
